import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { useQueries } from "@tanstack/react-query";
import { zohoApi } from "zohoApi";
import { useZohoAuthContext } from "context/ZohoAuthContext";

const getTransformFn = ({
  startTimeFieldApi,
  endTimeFieldApi,
  startTimeDataType,
  endTimeDataType,
  //
  title,
  //
  module,
  //
  ...rest
}) => {
  const getTime = ({ start, end, time, dataType }) => {
    // console.log({ dataType, start, end, time });
    if (dataType === "datetime") {
      return dayjs(time).format("YYYY-MM-DDTHH:mm");
    }

    if (dataType === "date" && start) {
      return `${dayjs(time).format("YYYY-MM-DD")}T00:00`;
    }

    if (dataType === "date" && end) {
      return `${dayjs(time).format("YYYY-MM-DD")}T23:59`;
    }
  };

  return (data) => {
    const titleKeyStrings = title?.match(/\$\{[^}]+\}/g);

    const transformedDataArr = data?.map((el) => {
      const url_with_org_domain = module.url_with_org_domain;
      const recordUrl = `${url_with_org_domain}${el?.id}`;
      let dynamicTitle = title;
      titleKeyStrings?.forEach((str) => {
        const key = str.slice(2, -1)?.split(".")?.[1];

        let value;

        if (key === "Owner") {
          value = el["Owner.profile.name"];
          // value = el["Owner.name"];
        } else {
          value = el[key] ?? "";
        }

        dynamicTitle = dynamicTitle.replace(str, value);
      });

      const start = getTime({
        start: true,
        time: el[startTimeFieldApi],
        dataType: startTimeDataType,
      });

      const end = getTime({
        end: true,
        time: el[endTimeFieldApi],
        dataType: endTimeDataType,
      });

      const temp = { ...el };
      delete temp.Owner;
      delete temp[startTimeFieldApi];
      delete temp[endTimeFieldApi];
      const resource = el["Owner.id"];

      const transformedObj = {
        ...temp,
        resource,
        start,
        end,
        title: dynamicTitle,
        startTimeDataType,
        endTimeDataType,
        recordUrl,
        ...rest,
      };

      return transformedObj;
    });

    return transformedDataArr;
  };
};

export const useGetEvents = ({ startTimeValue, endTimeValue, selectedSettings }) => {
  const { accessToken } = useZohoAuthContext();
  const queryClient = useQueryClient();

  const queries = selectedSettings?.map((setting) => {
    const { title, queryObj, deletable, setting_id, dragToResize: resize, dragToMove: dragInTime, module } = setting || {};

    const {
      fieldApis,
      startTimeFieldApi,
      endTimeFieldApi,
      startTimeDataType,
      endTimeDataType,
      eventColor: color,
      moduleApi: moduleApiName,
    } = queryObj || {};

    const titleKeyStrings = title?.match(/\$\{[^}]+\}/g);

    const titleKeys =
      titleKeyStrings?.map((str) => {
        return str.slice(2, -1)?.split(".")?.[1];
      }) || [];

    const uniqueFields = [...new Set([...fieldApis, ...titleKeys])]?.filter((item) => item !== "Owner");

    const fields = `${uniqueFields?.join(",")},Owner.profile.name,Owner.id `;
    // const fields = `${uniqueFields?.join(",")},Owner.name,Owner.id `;

    const configKey = `${moduleApiName} : ${startTimeFieldApi} >= ${startTimeValue} && ${endTimeFieldApi} <= ${endTimeValue}`;

    const configEnabled = !!startTimeValue && !!endTimeValue && !!accessToken;

    const startTime = startTimeDataType === "datetime" ? `'${startTimeValue}T00:00:00+00:00'` : `'${startTimeValue}'`;

    const endTime = endTimeDataType === "datetime" ? `'${endTimeValue}T23:59:59+00:00'` : `'${endTimeValue}'`;

    const condition = `(${startTimeFieldApi} >= ${startTime} AND ${endTimeFieldApi} <= ${endTime})`;

    const select_query = `SELECT ${fields} from ${moduleApiName} where ${condition}`;

    const config = {
      startTimeFieldApi,
      startTimeDataType,
      endTimeFieldApi,
      endTimeDataType,
      //
      configKey,
      color,
      moduleApiName,
      deletable,
      setting_id,
      resize,
      dragInTime,
      //
      title,
      //
      module,
    };

    return {
      queryKey: ["eventArray", configKey],
      staleTime: Infinity,
      enabled: configEnabled,
      queryFn: async () => {
        const { data } = await zohoApi.coql.getDataByCoql({
          accessToken,
          select_query,
        });

        if (data?.length > 0) {
          const transformData = getTransformFn(config);
          return transformData(data);
        }
        return data;
      },
    };
  });

  const results = useQueries({ queries });

  //  const transformedArray = results
  //   ?.map((el) => el?.data)
  //   ?.filter((el) => el?.length > 0)
  //   ?.flat();

  // return { data: transformedArray, loading: false };

  const isInitialLoading = results.some((result) => result.isLoading);
  const isRefetching = results.some((r) => r.isFetching);

  if (!isInitialLoading && !isRefetching) {
    const allEvent = queryClient
      .getQueryCache()
      .getAll()
      ?.filter((el) => {
        return el?.queryKey?.includes("eventArray") && Object.keys(el?.state?.data || {}).length > 0;
      })
      ?.map((el) => el?.state?.data)
      ?.flat();

    const uniqueEvents = [...new Map(allEvent?.map((obj) => [obj.id, obj])).values()];

    return { data: uniqueEvents, loading: isInitialLoading, isRefetching };
  }

  return { data: [], loading: isInitialLoading, isRefetching };
};
